import { Component, OnInit, ChangeDetectionStrategy, inject, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { TableModule } from 'primeng/table';
import { SelectModule } from 'primeng/select';
import { ButtonModule } from 'primeng/button';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { DrawerModule } from 'primeng/drawer';
import { TooltipModule } from 'primeng/tooltip';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { TagModule } from 'primeng/tag';
import { WebhooksService } from '@generated/services/webhooks.service';
import { WebhookDto } from '@generated/models/webhook-dto';
import { WebhookRequestDto } from '@generated/models/webhook-request-dto';
import { WebhookRequestDetailDto } from '@generated/models/webhook-request-detail-dto';
import { NotificationService } from '@core/services/notification.service';

@Component({
  selector: 'app-webhook-requests',
  imports: [
    CommonModule,
    FormsModule,
    TableModule,
    SelectModule,
    ButtonModule,
    ProgressSpinnerModule,
    DrawerModule,
    TooltipModule,
    BreadcrumbModule,
    TagModule
  ],
  templateUrl: './webhook-requests.component.html',
  styleUrl: './webhook-requests.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class WebhookRequestsComponent implements OnInit {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private webhooksService = inject(WebhooksService);
  private notificationService = inject(NotificationService);
  private cdr = inject(ChangeDetectorRef);

  // Webhooks dropdown
  webhooks: WebhookDto[] = [];
  webhookOptions: { label: string; value: string }[] = [];
  selectedWebhookId: string | null = null;

  // Requests data
  requests: WebhookRequestDto[] = [];
  isLoading = false;
  nextCursor: string | null = null;
  readonly ROWS_PER_PAGE = 20;

  virtualScrollOptions = {
    lazy: true,
    onLazyLoad: this.onLazyLoad.bind(this),
    itemSize: 60,
    delay: 150,
    showLoader: false,
    loading: false
  };

  // Drawer
  showDrawer = false;
  drawerLoading = false;
  selectedRequest: WebhookRequestDetailDto | null = null;

  // Breadcrumb
  breadcrumbItems: any[] = [
    { label: $localize`:@@nav.developers:Developers`, routerLink: '/developers' },
    { label: $localize`:@@webhookRequests.title:Webhook Requests` }
  ];

  ngOnInit(): void {
    const webhookId = this.route.snapshot.paramMap.get('webhookId');
    if (webhookId) {
      this.selectedWebhookId = webhookId;
    }
    this.loadWebhooks();
  }

  private loadWebhooks(): void {
    this.webhooksService.apiWebhooksGet().subscribe({
      next: (webhooks) => {
        this.webhooks = webhooks || [];
        this.webhookOptions = this.webhooks.map(w => ({
          label: w.url || 'No URL',
          value: w.id || ''
        })).filter(opt => opt.value);

        if (this.selectedWebhookId) {
          this.loadRequests();
        }
        this.cdr.markForCheck();
      },
      error: () => {
        this.notificationService.showError($localize`:@@developers.notify.loadFail:Failed to load webhooks`);
        this.cdr.markForCheck();
      }
    });
  }

  onWebhookChange(): void {
    this.requests = [];
    this.nextCursor = null;
    if (this.selectedWebhookId) {
      this.loadRequests();
    }
  }

  onLazyLoad(event: any): void {
    const last = event.last || 0;
    const currentLength = this.requests.length;

    if (currentLength > 0 && last >= currentLength - 5 && this.nextCursor && !this.isLoading) {
      this.loadRequests();
    }
  }

  private loadRequests(): void {
    if (this.isLoading || !this.selectedWebhookId) return;
    if (this.requests.length > 0 && !this.nextCursor) return;

    this.isLoading = true;
    this.cdr.markForCheck();

    this.webhooksService.apiWebhooksWebhookIdRequestsGet({
      webhookId: this.selectedWebhookId,
      cursor: this.nextCursor || undefined,
      limit: this.ROWS_PER_PAGE
    }).subscribe({
      next: (result) => {
        const isFirstLoad = this.requests.length === 0;

        if (isFirstLoad) {
          this.requests = [...(result.items || [])];
        } else {
          this.requests.push(...(result.items || []));
        }

        this.nextCursor = result.next_cursor || null;
        this.isLoading = false;
        this.cdr.markForCheck();
      },
      error: () => {
        this.notificationService.showError($localize`:@@webhookRequests.notify.loadFail:Failed to load webhook requests`);
        this.isLoading = false;
        this.cdr.markForCheck();
      }
    });
  }

  viewRequest(request: WebhookRequestDto, event: Event): void {
    event.stopPropagation();
    if (!request.id) return;

    this.showDrawer = true;
    this.drawerLoading = true;
    this.selectedRequest = null;
    this.cdr.markForCheck();

    this.webhooksService.apiWebhooksRequestsIdGet({ id: request.id }).subscribe({
      next: (detail) => {
        this.selectedRequest = detail;
        this.drawerLoading = false;
        this.cdr.markForCheck();
      },
      error: () => {
        this.notificationService.showError($localize`:@@webhookRequests.notify.detailsFail:Failed to load request details`);
        this.drawerLoading = false;
        this.cdr.markForCheck();
      }
    });
  }

  getFormattedBody(): string {
    if (!this.selectedRequest?.body) return '';
    try {
      const parsed = JSON.parse(this.selectedRequest.body);
      return JSON.stringify(parsed, null, 2);
    } catch {
      return this.selectedRequest.body;
    }
  }

  copyBody(): void {
    const body = this.getFormattedBody();
    navigator.clipboard.writeText(body).then(() => {
      this.notificationService.showSuccess($localize`:@@webhookRequests.notify.copied:Copied to clipboard`);
    });
  }

  closeDrawer(): void {
    this.showDrawer = false;
    this.selectedRequest = null;
    this.cdr.markForCheck();
  }

  goBack(): void {
    this.router.navigate(['/developers']);
  }

  trackByRequestId(index: number, request: WebhookRequestDto): string | number {
    return request?.id || index;
  }

  getStatusSeverity(status: string | undefined): 'success' | 'danger' | 'secondary' | 'warn' | 'info' | 'contrast' {
    switch (status) {
      case 'Completed': return 'success';
      case 'Started': return 'warn';
      default: return 'secondary';
    }
  }

  readonly unknownStatusLabel = $localize`:@@conversations.status.unknown:Unknown`;
  readonly errorLabel = $localize`:@@campaignItems.col.error:Error`;

  getStatusLabel(status: string | undefined): string {
    if (!status) return this.unknownStatusLabel;
    switch (status) {
      case 'Completed': return $localize`:@@conversations.status.completed:Completed`;
      case 'Started': return $localize`:@@webhookRequests.status.started:Started`;
      case 'Failed': return $localize`:@@campaigns.status.failed:Failed`;
      default: return status;
    }
  }

  getHttpStatusClass(status: number | null | undefined): string {
    if (!status) return '';
    if (status >= 200 && status < 300) return 'http-success';
    if (status >= 400 && status < 500) return 'http-client-error';
    if (status >= 500) return 'http-server-error';
    return '';
  }
}
